import {
  type ChaveDaFaixa,
  CHAVES_DE_FAIXA,
  FAIXAS_DE_DURACAO,
  faixasFaltantes,
  fromCents,
  fromWire,
  ROTULO_DA_FAIXA,
  salvarTarifasSchema,
  tabelaNaoCrescente,
  type TarifaDaFaixa,
} from '@locaveri/shared'
import {
  CircleAlert,
  CircleCheck,
  CircleSlash,
  LoaderCircle,
  Pencil,
  Table2,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useForm, type UseFormSetError } from 'react-hook-form'
import { toast } from 'sonner'
import type { z } from 'zod'

import { Campo } from '@/components/form/campo'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useCategoriasAtivas } from '@/features/frota/api'
import { errorMessage } from '@/lib/api'
import { SEM_VALOR } from '@/lib/datas'
import { deCampoDeReaisParaWire, formatarMoedaDoWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi } from '@/lib/formulario'

import {
  type TabelaDePreco,
  type TarifasDaCategoria,
  useRemoverTarifasDaCategoria,
  useSalvarTarifas,
  useTarifasDaTabela,
} from './api'

/**
 * O editor de tarifas: a grade CATEGORIA × FAIXA de uma tabela de preço.
 *
 * ---------------------------------------------------------------------------
 * SALVA-SE A CATEGORIA INTEIRA, NUNCA UMA FAIXA
 * ---------------------------------------------------------------------------
 * "A diária não sobe ao alongar a locação" é a única promessa que o cliente lê
 * literalmente na tabela — e é propriedade do CONJUNTO, não de uma linha.
 * Editar faixa a faixa faria a tabela passar por estados inválidos que ninguém
 * observa, e um deles cotaria: entre o `PATCH` que baixa a faixa de 3 a 6 e o
 * que baixa a de 7 a 14, existe um instante em que a tabela cobra mais caro por
 * dia para ficar mais tempo. Por isso o formulário é a linha inteira e o envio
 * é um `PUT` de substituição.
 *
 * ---------------------------------------------------------------------------
 * A TELA VALIDA ANTES DE VIAJAR — E O ERRO CAI NA CÉLULA
 * ---------------------------------------------------------------------------
 * `tabelaNaoCrescente` é a MESMA função que a API chama antes de gravar; o
 * servidor recusa de qualquer jeito. Rodá-la aqui não é desconfiança do
 * servidor: é o que permite dizer QUAL das cinco células está errada e contra
 * qual outra, em vez de devolver um 422 genérico sobre um formulário de cinco
 * campos. O veredito é sempre dela — a função que localiza o par existe só para
 * escrever a frase, e só roda depois do veredito negativo.
 *
 * `faixasFaltantes` AVISA, não bloqueia: uma locadora pode legitimamente não
 * alugar por trinta dias. Mas ausência não aparece numa lista de linhas, e sem
 * o aviso o buraco só apareceria no dia em que um cliente pedisse sete diárias
 * e a categoria simplesmente não fosse cotável.
 *
 * ---------------------------------------------------------------------------
 * A GRADE MOSTRA A FROTA, NÃO SÓ O QUE JÁ TEM PREÇO
 * ---------------------------------------------------------------------------
 * O endpoint devolve apenas as categorias COM tarifa — ausência não é linha. Se
 * a tela mostrasse só isso, a categoria sem preço (a que mais precisa de
 * atenção) seria invisível. Por isso a grade parte do catálogo de categorias e
 * junta as tarifas por cima; e uma categoria fora do catálogo que ainda tenha
 * tarifa gravada aqui continua aparecendo, marcada — some da vitrine, não da
 * tabela.
 *
 * Dinheiro entra e sai como STRING de centavos (invariante nº 1). Nenhuma
 * aritmética monetária acontece neste arquivo: o único `bigint` que aparece é o
 * da COMPARAÇÃO de `tabelaNaoCrescente`, e ele vem de `fromWire`.
 */

type FormularioDeTarifas = Record<ChaveDaFaixa, string>

/**
 * A conversão é só de forma: `Object.fromEntries` perde a chave literal, e as
 * entradas vêm de `CHAVES_DE_FAIXA`, que já é exatamente `ChaveDaFaixa`.
 */
const FORMULARIO_VAZIO = Object.fromEntries(
  CHAVES_DE_FAIXA.map((faixa) => [faixa, '']),
) as FormularioDeTarifas

/**
 * `DE_7_A_14` → `7 a 14`.
 *
 * O rótulo completo ("7 a 14 diárias") é o do cabeçalho da coluna; dentro de
 * uma frase de erro ele viraria "a diária de 7 a 14 diárias não pode ser maior
 * que a de 3 a 6 diárias". Sai de `FAIXAS_DE_DURACAO` como tudo mais — uma
 * faixa nova no motor não pode depender de alguém lembrar deste mapa.
 */
const FAIXA_EM_DIARIAS = Object.fromEntries(
  FAIXAS_DE_DURACAO.map((faixa) => [
    faixa.chave,
    faixa.maximo === null
      ? `${String(faixa.minimo)} ou mais`
      : `${String(faixa.minimo)} a ${String(faixa.maximo)}`,
  ]),
) as Readonly<Record<ChaveDaFaixa, string>>

interface ConflitoDeFaixa {
  readonly faixa: ChaveDaFaixa
  readonly anterior: ChaveDaFaixa
}

/**
 * Qual faixa quebra a promessa, e contra qual.
 *
 * NÃO é uma segunda validação: quem decide se a tabela pode ser gravada é
 * `tabelaNaoCrescente`, e esta função só roda depois de ela dizer não. Percorre
 * na ORDEM DECLARADA das faixas (não na ordem do formulário) porque é essa a
 * ordem em que a duração cresce.
 */
function faixaQueSobe(tarifas: readonly TarifaDaFaixa[]): ConflitoDeFaixa | null {
  const naOrdemDoMotor = FAIXAS_DE_DURACAO.map((faixa) =>
    tarifas.find((tarifa) => tarifa.faixa === faixa.chave),
  ).filter((tarifa) => tarifa !== undefined)

  let anterior: TarifaDaFaixa | null = null

  for (const tarifa of naOrdemDoMotor) {
    if (anterior !== null && tarifa.diaria > anterior.diaria) {
      return { faixa: tarifa.faixa, anterior: anterior.faixa }
    }

    anterior = tarifa
  }

  return null
}

/**
 * Espalha os erros do `safeParse` pelas CÉLULAS.
 *
 * `aplicarErrosDoZod` não serve aqui: os caminhos de `salvarTarifasSchema` são
 * `tarifas.0.diaria` — índices do array ENVIADO —, e o formulário é indexado por
 * faixa. Sem esta tradução, "a diária precisa ser maior que zero" apareceria no
 * alerta do topo sem dizer qual das cinco células está zerada.
 */
function espalharErrosDasTarifas(
  erro: z.ZodError,
  faixasEnviadas: readonly ChaveDaFaixa[],
  setError: UseFormSetError<FormularioDeTarifas>,
): string | null {
  const sobras: string[] = []

  for (const issue of erro.issues) {
    const indice = issue.path[1]
    const faixa = typeof indice === 'number' ? faixasEnviadas[indice] : undefined

    if (issue.path[0] === 'tarifas' && faixa !== undefined) {
      setError(faixa, { type: 'validate', message: issue.message })
    } else {
      sobras.push(issue.message)
    }
  }

  return sobras.length > 0 ? sobras.join(' · ') : null
}

interface LinhaDaGrade {
  readonly categoryId: string
  readonly nome: string
  /** Categoria fora do catálogo que ainda tem tarifa gravada nesta tabela. */
  readonly foraDoCatalogo: boolean
  /** Centavos de wire por faixa. Faixa sem tarifa simplesmente não tem chave. */
  readonly porFaixa: Readonly<Partial<Record<ChaveDaFaixa, string>>>
  readonly faixasFaltantes: readonly ChaveDaFaixa[]
  /** `true` é o estado BOM — ver o comentário de `tarifasDaCategoriaSchema`. */
  readonly naoCrescente: boolean
  readonly temTarifa: boolean
}

function montarLinha(
  categoryId: string,
  nome: string,
  tarifas: TarifasDaCategoria | undefined,
  foraDoCatalogo: boolean,
): LinhaDaGrade {
  const porFaixa: Partial<Record<ChaveDaFaixa, string>> = {}

  for (const item of tarifas?.itens ?? []) {
    porFaixa[item.faixa] = item.diaria
  }

  return {
    categoryId,
    nome,
    foraDoCatalogo,
    porFaixa,
    // Categoria sem nenhuma tarifa não tem linha no servidor: aqui ela falta em
    // TODAS as faixas, e `naoCrescente` é verdade vazia.
    faixasFaltantes: tarifas?.faixasFaltantes ?? CHAVES_DE_FAIXA,
    naoCrescente: tarifas?.naoCrescente ?? true,
    temTarifa: (tarifas?.itens.length ?? 0) > 0,
  }
}

function valoresDaLinha(linha: LinhaDaGrade | null): FormularioDeTarifas {
  const valores = { ...FORMULARIO_VAZIO }

  if (linha === null) {
    return valores
  }

  for (const faixa of CHAVES_DE_FAIXA) {
    valores[faixa] = paraCampoDeReais(linha.porFaixa[faixa])
  }

  return valores
}

/** Selo da linha: cor + ícone + rótulo, nunca cor sozinha. */
function SituacaoDaLinha({ linha }: { readonly linha: LinhaDaGrade }): ReactElement {
  if (!linha.temTarifa) {
    return (
      <Badge variant="warning" title="Nenhuma faixa preenchida — a categoria não cota nesta tabela">
        <TriangleAlert aria-hidden="true" />
        Sem preço
      </Badge>
    )
  }

  if (!linha.naoCrescente) {
    return (
      <Badge variant="danger" title="Há faixa cuja diária é maior que a de uma locação mais curta">
        <CircleAlert aria-hidden="true" />
        Diária sobe
      </Badge>
    )
  }

  if (linha.faixasFaltantes.length > 0) {
    return (
      <Badge variant="warning" title="Locações destas durações não são cotáveis nesta tabela">
        <TriangleAlert aria-hidden="true" />
        {linha.faixasFaltantes.length === 1
          ? 'Falta 1 faixa'
          : `Faltam ${String(linha.faixasFaltantes.length)} faixas`}
      </Badge>
    )
  }

  return (
    <Badge variant="success">
      <CircleCheck aria-hidden="true" />
      Completa
    </Badge>
  )
}

interface DialogoDeRemocaoProps {
  readonly tabela: TabelaDePreco
  /** `null` mantém fechado. */
  readonly linha: LinhaDaGrade | null
  readonly aoFechar: () => void
}

/**
 * Confirmação da remoção.
 *
 * Aqui as linhas SAEM mesmo, ao contrário das tabelas e das regras: nada aponta
 * para uma tarifa — a cotação congela o VALOR da diária, não o id da linha que a
 * produziu, e o instantâneo completo fica no `audit_log`. O que a confirmação
 * precisa dizer em voz alta é a consequência: depois disso a categoria deixa de
 * ser cotável nesta tabela, e isso não aparece em lugar nenhum até um cliente
 * pedir aquele carro.
 */
function DialogoDeRemocao({ tabela, linha, aoFechar }: DialogoDeRemocaoProps): ReactElement {
  const remover = useRemoverTarifasDaCategoria()
  const [erro, setErro] = useState<string | null>(null)
  const ocupado = remover.isPending

  // A dependência é o ID, e não a linha: a grade é remontada a cada render, e
  // com o objeto aqui o efeito rodaria sempre — apagando, no render seguinte, o
  // erro que o `onError` acabou de escrever.
  const categoriaDaConfirmacao = linha?.categoryId ?? null

  // O diálogo é reaproveitado por todas as linhas: sem esta limpeza, a falha ao
  // remover a categoria A apareceria na confirmação da categoria B.
  useEffect(() => {
    setErro(null)
  }, [categoriaDaConfirmacao])

  function confirmar(): void {
    if (linha === null) {
      return
    }

    setErro(null)

    remover.mutate(
      { priceTableId: tabela.id, categoryId: linha.categoryId },
      {
        onSuccess: () => {
          toast.success(`${linha.nome} saiu de ${tabela.nome}`, {
            description: 'A categoria deixa de ser cotável nesta tabela até ganhar tarifa de novo.',
          })
          aoFechar()
        },
        // O erro fica NO diálogo: um toast sumiria e a tela voltaria a
        // perguntar a mesma coisa, como se nada tivesse acontecido.
        onError: (causa: unknown) => {
          setErro(errorMessage(causa))
        },
      },
    )
  }

  return (
    <Dialog
      open={linha !== null}
      onOpenChange={(proximo) => {
        if (!proximo && !ocupado) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tirar {linha?.nome ?? 'a categoria'} desta tabela?</DialogTitle>
          <DialogDescription>
            As faixas saem todas juntas — tarifa é conjunto, nunca linha solta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm text-fg-secondary">
          {erro === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          <p>
            Cotação já emitida não muda: ela guarda o valor que cobrou, não a tarifa que o produziu.
            O instantâneo do que estava gravado fica no log de auditoria.
          </p>

          <Alert variant="warning">
            <AlertDescription>
              Sem tarifa nesta tabela, a categoria para de ser cotável para quem é precificado por
              ela — e isso só apareceria quando um cliente pedisse esse carro.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
            Cancelar
          </Button>
          <Button type="button" variant="destructive" onClick={confirmar} disabled={ocupado}>
            {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            Remover tarifas
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export interface EditorDeTarifasProps {
  /** `null` enquanto nenhuma tabela está selecionada na lista. */
  readonly tabela: TabelaDePreco | null
  /**
   * Situação da LISTA de tabelas, que vive na página.
   *
   * `tabela` é `null` em três situações muito diferentes: a lista ainda está
   * carregando, a lista falhou, ou a lista veio e nada está selecionado. Sem
   * distingui-las, o editor mostraria "escolha uma tabela na lista acima" por
   * cima de esqueletos ou de um erro — um estado VAZIO no lugar de um de
   * carregamento ou de erro, que é exatamente o que a DoD proíbe.
   */
  readonly listaDeTabelas: 'carregando' | 'erro' | 'pronta'
  readonly podeEditar: boolean
  readonly podeRemover: boolean
}

export function EditorDeTarifas({
  tabela,
  listaDeTabelas,
  podeEditar,
  podeRemover,
}: EditorDeTarifasProps): ReactElement {
  const consulta = useTarifasDaTabela(tabela?.id ?? null)
  /**
   * O catálogo vem do módulo de frota de propósito: é a MESMA consulta que a
   * tela de categorias e o formulário de veículo usam, e uma cópia local
   * duplicaria a requisição e o cache para responder a mesma pergunta.
   */
  const categorias = useCategoriasAtivas()
  const salvar = useSalvarTarifas()

  const [categoriaEmEdicao, setCategoriaEmEdicao] = useState<string | null>(null)
  const [categoriaParaRemover, setCategoriaParaRemover] = useState<string | null>(null)
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  // O editor abre acima de uma tabela que, em 375px, pode estar duas telas
  // abaixo: sem levar a página até ele, o clique não parece ter feito nada.
  const painel = useRef<HTMLDivElement>(null)

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormularioDeTarifas>({ defaultValues: FORMULARIO_VAZIO })

  const itensDaTabela = consulta.data?.itens ?? []
  const categoriasDoCatalogo = categorias.data?.itens ?? []

  const linhas: readonly LinhaDaGrade[] = [
    ...categoriasDoCatalogo.map((categoria) =>
      montarLinha(
        categoria.id,
        categoria.nome,
        itensDaTabela.find((item) => item.categoria.id === categoria.id),
        false,
      ),
    ),
    ...itensDaTabela
      .filter(
        (item) => !categoriasDoCatalogo.some((categoria) => categoria.id === item.categoria.id),
      )
      .map((item) => montarLinha(item.categoria.id, item.categoria.nome, item, true)),
  ]

  // Derivada da lista fresca, e não guardada em estado: assim o painel aberto
  // acompanha o que o servidor devolveu depois de salvar.
  const linhaEmEdicao = linhas.find((linha) => linha.categoryId === categoriaEmEdicao) ?? null
  const linhaParaRemover = linhas.find((linha) => linha.categoryId === categoriaParaRemover) ?? null
  const crescentes = linhas.filter((linha) => linha.temTarifa && !linha.naoCrescente)
  /** Nenhuma das duas permissões = a coluna de ações não tem por que existir. */
  const temAcoes = podeEditar || podeRemover

  useEffect(() => {
    if (categoriaEmEdicao !== null) {
      painel.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [categoriaEmEdicao])

  const valoresDigitados = watch()

  /**
   * As faixas preenchidas AGORA, para o aviso ao vivo de faixa faltante.
   *
   * Valor ilegível continua OCUPANDO a faixa (entra com zero, que não é usado
   * para nada aqui): quem está digitando "45" a caminho de "450,00" não pode ver
   * "falta a faixa de 3 a 6" piscando a cada tecla. Esta lista NUNCA decide se a
   * tabela é crescente — para isso, só a leitura estrita do envio serve.
   */
  const tarifasDigitadas: TarifaDaFaixa[] = []

  for (const faixa of CHAVES_DE_FAIXA) {
    const texto = valoresDigitados[faixa].trim()

    if (texto.length === 0) {
      continue
    }

    const wire = deCampoDeReaisParaWire(texto)

    tarifasDigitadas.push({ faixa, diaria: wire === null ? fromCents(0) : fromWire(wire) })
  }

  const faltantesAoVivo = faixasFaltantes(tarifasDigitadas)
  const ocupado = salvar.isPending

  function abrirEdicao(linha: LinhaDaGrade): void {
    setErroGeral(null)
    // O `reset` acontece no clique, e não num efeito: assim o formulário nunca
    // fica um render com os valores da categoria anterior.
    reset(valoresDaLinha(linha))
    setCategoriaEmEdicao(linha.categoryId)
  }

  function fecharEdicao(): void {
    setErroGeral(null)
    setCategoriaEmEdicao(null)
    reset(FORMULARIO_VAZIO)
  }

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    if (tabela === null || linhaEmEdicao === null) {
      return
    }

    // O formato de cada campo já passou pelo `validate` do register; aqui só
    // sobra converter. `deCampoDeReaisParaWire` devolvendo `null` neste ponto
    // significaria campo vazio, que é ausência de faixa, não erro.
    const enviadas: { readonly faixa: ChaveDaFaixa; readonly diaria: string }[] = []

    for (const faixa of CHAVES_DE_FAIXA) {
      const wire = deCampoDeReaisParaWire(valores[faixa])

      if (wire !== null) {
        enviadas.push({ faixa, diaria: wire })
      }
    }

    if (enviadas.length === 0) {
      setErroGeral(
        'Informe a diária de ao menos uma faixa. Para tirar a categoria da tabela, use “Remover”.',
      )
      return
    }

    const paraConferir: TarifaDaFaixa[] = enviadas.map((tarifa) => ({
      faixa: tarifa.faixa,
      diaria: fromWire(tarifa.diaria),
    }))

    // O veredito é da função do domínio — a MESMA que a API roda antes de
    // gravar. A localização do par existe só para a frase caber na célula.
    if (!tabelaNaoCrescente(paraConferir)) {
      const conflito = faixaQueSobe(paraConferir)

      if (conflito === null) {
        setErroGeral(
          'A diária não pode subir ao alongar a locação. Confira as faixas em ordem crescente de duração.',
        )
        return
      }

      setError(conflito.faixa, {
        type: 'validate',
        message: `a diária de ${FAIXA_EM_DIARIAS[conflito.faixa]} não pode ser maior que a de ${FAIXA_EM_DIARIAS[conflito.anterior]}`,
      })
      return
    }

    const entrada = {
      categoryId: linhaEmEdicao.categoryId,
      tarifas: enviadas.map((tarifa) => ({ faixa: tarifa.faixa, diaria: tarifa.diaria })),
    }

    const validado = salvarTarifasSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(
        espalharErrosDasTarifas(
          validado.error,
          enviadas.map((tarifa) => tarifa.faixa),
          setError,
        ),
      )
      return
    }

    // `entrada`, e não `validado.data`: `salvarTarifasSchema` transforma cada
    // diária em `bigint`, e `JSON.stringify` de um `bigint` LANÇA.
    salvar.mutate(
      { priceTableId: tabela.id, dados: entrada },
      {
        onSuccess: (salva) => {
          toast.success(`Tarifas de ${salva.categoria.nome} salvas`, {
            description:
              salva.faixasFaltantes.length === 0
                ? 'As cinco faixas estão preenchidas: a categoria cota em qualquer duração nesta tabela.'
                : `Ainda sem tarifa em ${salva.faixasFaltantes.map((faixa) => ROTULO_DA_FAIXA[faixa]).join(', ')}.`,
          })
          fecharEdicao()
        },
        onError: (causa: unknown) => {
          setErroGeral(aplicarErrosDaApi(causa, setError, CHAVES_DE_FAIXA))
        },
      },
    )
  })

  return (
    <section aria-labelledby="titulo-das-tarifas" className="flex flex-col gap-4">
      <div className="min-w-0">
        <h2 id="titulo-das-tarifas" className="text-base font-semibold tracking-tight">
          Tarifas por faixa {tabela === null ? '' : `· ${tabela.nome}`}
        </h2>
        <p className="max-w-prose text-sm text-fg-secondary">
          A diária cheia de cada faixa de duração, por categoria. Salvar substitui todas as faixas
          da categoria de uma vez — “a diária não sobe ao alongar” é regra do conjunto.
        </p>
      </div>

      {listaDeTabelas === 'carregando' ? (
        <LoadingState rotulo="Carregando as tabelas de preço…" linhas={3} />
      ) : listaDeTabelas === 'erro' ? (
        // A mensagem e o "tentar de novo" já estão no erro da LISTA, logo
        // acima: repeti-los aqui daria dois botões para o mesmo refetch.
        <EmptyState
          icone={Table2}
          titulo="A lista de tabelas não carregou"
          descricao="Sem ela não há como saber qual tabela editar. Use “Tentar de novo” na lista acima — a mensagem do erro está lá."
        />
      ) : tabela === null ? (
        <EmptyState
          icone={Table2}
          titulo="Nenhuma tabela selecionada"
          descricao="Escolha uma tabela na lista acima — ou crie a primeira — para ver e editar a diária de cada categoria por faixa de duração."
        />
      ) : consulta.isPending || categorias.isPending ? (
        <LoadingState rotulo="Carregando as tarifas da tabela…" linhas={4} />
      ) : consulta.isError ? (
        <ErrorState
          titulo="Não foi possível carregar as tarifas"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      ) : (
        <>
          {/* A grade depende do catálogo para mostrar a categoria SEM preço —
              a falha dele não derruba a tela, mas precisa ser dita: sem o
              catálogo, uma categoria sem tarifa fica invisível. */}
          {categorias.isError ? (
            <Alert variant="warning">
              <AlertTitle>Não foi possível carregar o catálogo de categorias</AlertTitle>
              <AlertDescription>
                A grade mostra só as categorias que já têm tarifa nesta tabela — as que estão sem
                preço ficaram de fora. {errorMessage(categorias.error)}
              </AlertDescription>
            </Alert>
          ) : null}

          {crescentes.length > 0 ? (
            <Alert variant="danger">
              <AlertTitle>
                {crescentes.length === 1
                  ? 'Uma categoria cobra mais caro por dia em locação mais longa'
                  : `${String(crescentes.length)} categorias cobram mais caro por dia em locação mais longa`}
              </AlertTitle>
              <AlertDescription>
                {crescentes.map((linha) => linha.nome).join(', ')}. A gravação recusa tabela
                crescente, então estas linhas entraram por outro caminho (importação, seed, tabela
                antiga) — e enquanto estiverem assim, elas cotam contra a promessa da tabela. Abra
                cada uma e salve de novo.
              </AlertDescription>
            </Alert>
          ) : null}

          {podeEditar && linhaEmEdicao !== null ? (
            <div ref={painel} className="rounded-lg border border-border-strong bg-surface p-4">
              <form
                noValidate
                onSubmit={(evento) => {
                  void aoEnviar(evento)
                }}
                className="space-y-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">
                      Tarifas de {linhaEmEdicao.nome}
                    </p>
                    <p className="text-xs text-fg-secondary">
                      Em reais, por diária. Deixe em branco a faixa que a locadora não aluga — e
                      lembre que o valor de cada faixa não pode ser maior que o da faixa anterior.
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    aria-label="Fechar o editor de tarifas"
                    onClick={fecharEdicao}
                    disabled={ocupado}
                  >
                    <X aria-hidden="true" />
                  </Button>
                </div>

                {erroGeral === null ? null : (
                  <Alert variant="danger">
                    <AlertDescription>{erroGeral}</AlertDescription>
                  </Alert>
                )}

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  {CHAVES_DE_FAIXA.map((faixa) => (
                    <Campo
                      key={faixa}
                      rotulo={ROTULO_DA_FAIXA[faixa]}
                      erro={errors[faixa]?.message}
                    >
                      {(campo) => (
                        <Input
                          {...campo}
                          {...register(faixa, {
                            validate: (valor: string) =>
                              valor.trim().length === 0 ||
                              deCampoDeReaisParaWire(valor) !== null ||
                              'valor inválido — use o formato 450,00',
                          })}
                          inputMode="decimal"
                          placeholder="0,00"
                          className="tabular-money"
                          disabled={ocupado}
                        />
                      )}
                    </Campo>
                  ))}
                </div>

                {faltantesAoVivo.length > 0 ? (
                  <Alert variant="warning">
                    <AlertTitle>
                      {faltantesAoVivo.length === 1
                        ? 'Uma faixa vai ficar sem tarifa'
                        : `${String(faltantesAoVivo.length)} faixas vão ficar sem tarifa`}
                    </AlertTitle>
                    <AlertDescription>
                      {faltantesAoVivo.map((faixa) => ROTULO_DA_FAIXA[faixa]).join(', ')}. Não é
                      erro — uma locadora pode não alugar nessas durações. Mas locação desse tamanho
                      não vai ser cotável nesta tabela.
                    </AlertDescription>
                  </Alert>
                ) : null}

                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={fecharEdicao}
                    disabled={ocupado}
                  >
                    Cancelar
                  </Button>
                  {/* O nome da categoria fica FORA do rótulo visível: `Button`
                      é `whitespace-nowrap`, e "Salvar tarifas de Blindado
                      Executivo Nível III" fica mais largo que 375px — a página
                      inteira ganharia rolagem horizontal. O título do painel
                      já diz de quem são as tarifas, e o `aria-label` mantém o
                      nome acessível para quem só ouve o botão. */}
                  <Button
                    type="submit"
                    disabled={ocupado}
                    aria-label={`Salvar as tarifas de ${linhaEmEdicao.nome}`}
                  >
                    {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
                    Salvar tarifas
                  </Button>
                </div>
              </form>
            </div>
          ) : null}

          {linhas.length === 0 ? (
            <EmptyState
              icone={Table2}
              titulo="Nenhuma categoria para precificar"
              descricao="Esta tabela não tem tarifa e o catálogo está vazio. Cadastre as categorias da frota antes de montar o preço — o cliente aluga categoria, nunca modelo."
            />
          ) : (
            <Table>
              <TableCaption className="sr-only">
                Diária por faixa de duração, em cada categoria da tabela {tabela.nome}
              </TableCaption>

              <TableHeader>
                <TableRow>
                  <TableHead>Categoria</TableHead>
                  {CHAVES_DE_FAIXA.map((faixa) => (
                    <TableHead key={faixa} className="text-right">
                      {ROTULO_DA_FAIXA[faixa]}
                    </TableHead>
                  ))}
                  <TableHead>Situação</TableHead>
                  {/* Sem `update` nem `delete` a coluna inteira seria um traço
                      repetido em cada linha — em 375px, uma coluna a mais de
                      rolagem para não dizer nada. */}
                  {temAcoes ? (
                    <TableHead>
                      <span className="sr-only">Ações</span>
                    </TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>

              <TableBody>
                {linhas.map((linha) => (
                  <TableRow key={linha.categoryId}>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{linha.nome}</p>
                        {linha.foraDoCatalogo ? (
                          <span className="mt-1 inline-flex">
                            <Badge
                              variant="neutral"
                              title="Categoria fora do catálogo — some da vitrine, mas a tarifa continua gravada aqui"
                            >
                              <CircleSlash aria-hidden="true" />
                              Fora do catálogo
                            </Badge>
                          </span>
                        ) : null}
                      </div>
                    </TableCell>

                    {CHAVES_DE_FAIXA.map((faixa) => {
                      const valor = linha.porFaixa[faixa]

                      return (
                        <TableCell key={faixa} className="money-cell">
                          {valor === undefined ? (
                            <span
                              className="text-fg-muted"
                              title="Sem tarifa: locação desta duração não é cotável nesta tabela"
                            >
                              {SEM_VALOR}
                            </span>
                          ) : (
                            formatarMoedaDoWire(valor)
                          )}
                        </TableCell>
                      )
                    })}

                    <TableCell>
                      <SituacaoDaLinha linha={linha} />
                    </TableCell>

                    {temAcoes ? (
                      <TableCell>
                        {podeEditar || (podeRemover && linha.temTarifa) ? (
                          <div className="flex flex-wrap justify-end gap-1">
                            {podeEditar ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  abrirEdicao(linha)
                                }}
                              >
                                <Pencil aria-hidden="true" />
                                {linha.temTarifa ? 'Editar' : 'Definir'}
                              </Button>
                            ) : null}

                            {podeRemover && linha.temTarifa ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Remover as tarifas de ${linha.nome}`}
                                onClick={() => {
                                  setCategoriaParaRemover(linha.categoryId)
                                }}
                              >
                                <Trash2 aria-hidden="true" />
                                Remover
                              </Button>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
                        )}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {tabela === null || !podeRemover ? null : (
        <DialogoDeRemocao
          tabela={tabela}
          linha={linhaParaRemover}
          aoFechar={() => {
            setCategoriaParaRemover(null)
          }}
        />
      )}
    </section>
  )
}
